import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useConfig } from '@/contexts/ConfigContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  User,
  Check,
  ArrowRight,
  Menu,
  X,
  Globe,
  ShieldCheck,
  BarChart3,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MagicContainer, MagicContainerContent, MagicContainerHeader, MagicContainerTitle } from '@/components/ui/MagicContainer';

// Asset Imports
const LOGO_FULL = "/assets/branding/logo-full-color.png";
const LOGO_WHITE = "/assets/branding/logo-white.png";
const LOGO_ICON = "/assets/branding/logo-icon.png";

const STRIPE_CHECKOUT_LINK = process.env.REACT_APP_STRIPE_CHECKOUT_LINK || 'https://buy.stripe.com/cNieVef4u21kaBheUw4F20l';

export default function Landing() {
  const { t, language, setLanguage } = useConfig();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle Scroll Effect for Navbar
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    setLanguage(lng);
  };

  const navigate = useNavigate(); // Hook de navegación

  const handleSubscribe = (role) => {
    if (role === 'agency') {
      // Redirigir al registro de agencia
      navigate('/login?register=true&role=agency_admin');
      return;
    }

    const params = new URLSearchParams({
      'success_url': `${window.location.origin}/subscription/plans?success=true&role=${role}`,
      'cancel_url': `${window.location.origin}/subscription/plans?canceled=true`
    });
    window.location.href = `${STRIPE_CHECKOUT_LINK}?${params.toString()}`;
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  // Get pricing features as arrays from translations
  const agencyFeatures = t('pricing.agency.features');
  const agentFeatures = t('pricing.agent.features');

  return (
    <div className="min-h-screen bg-transparent font-sans text-slate-900 selection:bg-orange-500 selection:text-white overflow-x-hidden">

      {/* Background Gradient */}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-50 via-white to-white dark:from-slate-900 dark:via-slate-950 dark:to-black" />

      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 dark:bg-slate-950/80 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
              <img
                src={LOGO_FULL}
                alt="Consoliday"
                className="h-10 w-auto group-hover:scale-105 transition-transform dark:hidden"
              />
              <img
                src={LOGO_WHITE}
                alt="Consoliday"
                className="h-10 w-auto group-hover:scale-105 transition-transform hidden dark:block"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-orange-600 dark:text-slate-300 dark:hover:text-orange-400 transition-colors">
                {t('landingNav.features')}
              </a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-orange-600 dark:text-slate-300 dark:hover:text-orange-400 transition-colors">
                {t('landingNav.pricing')}
              </a>

              {/* Language Switcher */}
              <div className="flex items-center gap-2 border-l border-slate-200 dark:border-slate-800 pl-6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => changeLanguage('es')}
                  className={language === 'es' ? 'font-bold text-orange-600' : 'text-slate-500'}
                >
                  ES
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => changeLanguage('en')}
                  className={language === 'en' ? 'font-bold text-orange-600' : 'text-slate-500'}
                >
                  EN
                </Button>
              </div>

              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="font-semibold text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800">
                    {t('landingNav.login')}
                  </Button>
                </Link>
                <Link to="/login?register=true">
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-500/20 rounded-full px-6">
                    {t('landingNav.register')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}>
                <Globe className="h-5 w-5" />
              </Button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-slate-900 dark:text-white p-2"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Content */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800"
            >
              <div className="px-4 pt-4 pb-8 space-y-4">
                <a href="#features" className="block text-base font-medium text-slate-900 dark:text-slate-100 p-2 rounded-lg hover:bg-slate-50">
                  {t('landingNav.features')}
                </a>
                <a href="#pricing" className="block text-base font-medium text-slate-900 dark:text-slate-100 p-2 rounded-lg hover:bg-slate-50">
                  {t('landingNav.pricing')}
                </a>
                <div className="h-px bg-slate-100 dark:bg-slate-800 my-4" />
                <Link to="/login" className="w-full">
                  <Button variant="outline" className="w-full justify-center mb-3">
                    {t('landingNav.login')}
                  </Button>
                </Link>
                <Link to="/login?register=true" className="w-full">
                  <Button className="w-full justify-center bg-orange-600 text-white">
                    {t('landingNav.register')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-48 lg:pb-32 px-4 overflow-hidden relative">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Badge Removed */}

          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-8 leading-tight"
          >
            {t('hero.titleStart')} <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              {t('hero.titleHighlight')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white h-14 px-8 text-lg rounded-full shadow-xl shadow-orange-500/20 transition-all hover:scale-105">
              {t('hero.ctaPrimary')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="h-14 px-8 text-lg rounded-full border-2 border-slate-200 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700 bg-white/50 backdrop-blur-sm">
              {t('hero.ctaSecondary')}
            </Button>
          </motion.div>
        </div>

        {/* Decorative Blobs */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-[800px] h-[800px] bg-orange-200/20 dark:bg-orange-900/10 rounded-full blur-[100px] animate-pulse" />
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white dark:bg-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              {t('features.title')}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<Zap className="w-8 h-8 text-orange-500" />}
              title={t('features.f1Title')}
              desc={t('features.f1Desc')}
            />
            <FeatureCard
              icon={<User className="w-8 h-8 text-blue-500" />}
              title={t('features.f2Title')}
              desc={t('features.f2Desc')}
            />
            <FeatureCard
              icon={<BarChart3 className="w-8 h-8 text-purple-500" />}
              title={t('features.f3Title')}
              desc={t('features.f3Desc')}
            />
            <FeatureCard
              icon={<ShieldCheck className="w-8 h-8 text-green-500" />}
              title={t('features.f4Title')}
              desc={t('features.f4Desc')}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Agency Card */}
            <PricingCard
              title={t('pricing.agency.title')}
              price={t('pricing.agency.price')}
              features={Array.isArray(agencyFeatures) ? agencyFeatures : []}
              buttonText={t('pricing.agency.btn')}
              icon={<Building2 className="w-6 h-6 text-white" />}
              color="orange"
              monthLabel={t('pricing.month')}
              onSubscribe={() => handleSubscribe('agency')}
            />

            {/* Agent Card */}
            <PricingCard
              title={t('pricing.agent.title')}
              price={t('pricing.agent.price')}
              features={Array.isArray(agentFeatures) ? agentFeatures : []}
              buttonText={t('pricing.agent.btn')}
              icon={<User className="w-6 h-6 text-white" />}
              color="teal"
              popular
              popularLabel={t('pricing.popular')}
              monthLabel={t('pricing.month')}
              onSubscribe={() => handleSubscribe('agent')}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img src={LOGO_ICON} alt="Icon" className="h-8 w-8 opacity-80" />
            <span className="font-semibold text-slate-900 dark:text-white">Consoliday © 2026</span>
          </div>
          <div className="flex gap-6 text-sm text-slate-500 dark:text-slate-400">
            <a href="#" className="hover:text-orange-600 transition-colors">{t('footer.privacy')}</a>
            <a href="#" className="hover:text-orange-600 transition-colors">{t('footer.terms')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-slate-50 dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-orange-200 dark:hover:border-orange-900/50 transition-colors shadow-sm"
    >
      <div className="bg-white dark:bg-slate-900 w-14 h-14 rounded-xl flex items-center justify-center shadow-sm mb-6">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{title}</h3>
      <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function PricingCard({ title, price, features, buttonText, icon, color, popular, popularLabel, monthLabel, onSubscribe }) {
  const isOrange = color === 'orange';
  const borderColor = isOrange ? 'border-orange-200 dark:border-orange-800' : 'border-teal-200 dark:border-teal-800';
  const shadowColor = isOrange ? 'shadow-orange-500/10' : 'shadow-teal-500/10';
  const btnColor = isOrange ? 'bg-orange-600 hover:bg-orange-700' : 'bg-teal-600 hover:bg-teal-700';

  return (
    <MagicContainer className={`relative overflow-hidden border-2 transition-all duration-300 hover:shadow-2xl ${borderColor} ${shadowColor} bg-white dark:bg-slate-900`}>
      {popular && (
        <div className="absolute top-0 right-0 bg-gradient-to-l from-teal-500 to-teal-600 text-white text-xs font-bold px-4 py-1 rounded-bl-xl shadow-lg">
          {popularLabel || 'POPULAR'}
        </div>
      )}
      <MagicContainerHeader className="text-center pb-2 pt-8">
        <div className={`w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center shadow-lg ${isOrange ? 'bg-orange-500' : 'bg-teal-500'}`}>
          {icon}
        </div>
        <MagicContainerTitle className="text-2xl font-bold text-slate-900 dark:text-white">{title}</MagicContainerTitle>
      </MagicContainerHeader>
      <MagicContainerContent className="space-y-8 px-8 pb-10">
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">{price}</span>
            <span className="text-lg text-slate-500 font-medium">{monthLabel || '/mo'}</span>
          </div>
        </div>
        <ul className="space-y-4">
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3">
              <div className={`p-1 rounded-full ${isOrange ? 'bg-orange-100 text-orange-600' : 'bg-teal-100 text-teal-600'}`}>
                <Check className="h-3 w-3" />
              </div>
              <span className="text-slate-700 dark:text-slate-300">{feature}</span>
            </li>
          ))}
        </ul>
        <Button onClick={onSubscribe} className={`w-full h-12 text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.02] ${btnColor}`}>
          {buttonText} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </MagicContainerContent>
    </MagicContainer>
  );
}
